// NOTICE: KAPTION-INTEGRATION
// message-pagination.DASHBOARD — the dashboard pages a conversation back in (created_at, id)
// order from a cursor that only server pages move. Message ids and dates disagree for rows
// inserted later with their original time (campaign templates materialized when the customer
// replies, whatsapp-sent-template-tracking.MATERIALIZATION.2, and coexistence history imports),
// so neither the smallest id nor the oldest message held in the store is a safe `before`.

export const isServerMessage = message =>
  Number.isSafeInteger(message?.id) && message.id > 0;

// Server order: created_at, then id. Pending (uuid) messages tie on equal timestamps.
export const compareMessagesByDate = (first, second) => {
  const byDate = Number(first?.created_at) - Number(second?.created_at);
  if (Number.isFinite(byDate) && byDate !== 0) return byDate;
  if (isServerMessage(first) && isServerMessage(second)) {
    return first.id - second.id;
  }
  return 0;
};

const pickServerMessage = (messages, isBetter) =>
  (messages || []).reduce((picked, message) => {
    if (!isServerMessage(message)) return picked;
    if (!picked || isBetter(compareMessagesByDate(message, picked))) {
      return message;
    }
    return picked;
  }, null);

export const getOldestServerMessage = messages =>
  pickServerMessage(messages, order => order < 0);

export const getNewestServerMessage = messages =>
  pickServerMessage(messages, order => order > 0);

// message-pagination.DASHBOARD.3 — where a message received outside a server page belongs.
// Pending messages and anything at or after the newest server message append, as before;
// an older server message (a template materialized with its send time, an update for a
// message that was never loaded) takes its (created_at, id) position instead of the end.
export const findMessageInsertIndex = (messages, message) => {
  const newest = getNewestServerMessage(messages);
  if (
    !isServerMessage(message) ||
    !newest ||
    compareMessagesByDate(message, newest) >= 0
  ) {
    return messages.length;
  }
  const index = messages.findIndex(
    existing =>
      isServerMessage(existing) && compareMessagesByDate(existing, message) > 0
  );
  return index === -1 ? messages.length : index;
};

// message-pagination.DASHBOARD.1 / COMPATIBILITY.1 — the cursor a server page leaves behind.
// The server names it in `meta.paging_cursor` (null when the page is not a contiguous date
// slice, e.g. an empty page); a server without the key falls back to the page's oldest message.
export const resolvePagingCursor = ({ meta, payload, currentCursor }) => {
  if (meta && 'paging_cursor' in meta) {
    return Number.isSafeInteger(meta.paging_cursor)
      ? meta.paging_cursor
      : currentCursor;
  }
  return getOldestServerMessage(payload)?.id ?? currentCursor;
};

// message-pagination.DASHBOARD.1 — `before` for the next "load older" request. The fallback
// (a conversation opened without a recorded cursor) re-reads from the newest server message:
// possibly redundant, never skipping.
export const getPagingCursor = chat =>
  chat?.pagingCursor ?? getNewestServerMessage(chat?.messages)?.id;
